@switch (state()) {
  @case ('loading') {
    <div class="flex flex-col gap-3 p-4" data-cy="history-loading">
      @for (row of skeletonRows; track row) {
        <div class="flex flex-col gap-2">
          <div class="h-3 w-3/4 animate-pulse rounded bg-border-2"></div>
          <div class="h-3 w-1/3 animate-pulse rounded bg-border-2"></div>
        </div>
      }
    </div>
  }

  @case ('error') {
    <div class="flex flex-col items-start gap-3 p-4" data-cy="history-error">
      <p class="text-sm">{{ 'store-history.we-could-not-load-your-history' | translate }}</p>

      <button mat-stroked-button data-cy="history-retry" (click)="retry.emit()">
        {{ 'common.retry' | translate }}
      </button>
    </div>
  }

  @case ('no-snapshot') {
    <div class="flex flex-col items-start gap-2 p-4" data-cy="history-no-snapshot">
      <p class="text-sm font-semibold">{{ 'store-history.history-starts-from-your-first-snapshot' | translate }}</p>
      <p class="text-sm text-brand-600">
        {{ 'store-history.we-take-a-snapshot-of-your-store-each-day-it-changes' | translate }}
      </p>
    </div>
  }

  @default {
    <div class="flex items-center gap-2 border-b border-solid border-border-2 px-4 py-2">
      <button
        mat-stroked-button
        data-cy="history-undo"
        [disabled]="!canUndo() || busy()"
        (click)="onUndo()"
        class="flex-1"
      >
        <mat-icon>undo</mat-icon>
        {{ 'common.undo' | translate }}
      </button>

      <button
        mat-stroked-button
        data-cy="history-redo"
        [disabled]="!canRedo() || busy()"
        (click)="onRedo()"
        class="flex-1"
      >
        <mat-icon>redo</mat-icon>
        {{ 'common.redo' | translate }}
      </button>
    </div>

    @if (draftEntries().length) {
      <div class="px-4 pb-1 pt-3 text-xs font-semibold uppercase text-brand-600" data-cy="history-unsaved-heading">
        {{ 'store-history.unsaved' | translate }}
      </div>

      @for (entry of draftEntries(); track entry.id) {
        <ng-container *ngTemplateOutlet="row; context: { $implicit: entry }" />
      }
    }

    <div class="px-4 pb-1 pt-3 text-xs font-semibold uppercase text-brand-600" data-cy="history-saved-heading">
      @if (snapshotAt(); as at) {
        {{ 'store-history.since-the-x-snapshot' | translate: { x: formatDate(at) } }}
      } @else {
        {{ 'store-history.earlier' | translate }}
      }
    </div>

    @if (state() === 'empty') {
      <p class="px-4 py-2 text-sm text-brand-600" data-cy="history-empty">
        {{ 'store-history.no-changes-since-then' | translate }}
      </p>
    } @else {
      @for (entry of savedEntries(); track entry.id) {
        <ng-container *ngTemplateOutlet="row; context: { $implicit: entry }" />
      }

      @if (hasMore()) {
        <div class="p-4">
          <button mat-stroked-button data-cy="history-load-more" [disabled]="loadingMore()" (click)="onLoadMore()">
            @if (loadingMore()) {
              <mat-spinner diameter="16" />
            } @else {
              {{ 'common.load-more' | translate }}
            }
          </button>
        </div>
      }
    }
  }
}

<ng-template #row let-entry>
  <div
    class="flex items-start gap-2 border-b border-solid border-border-2 px-4 py-2"
    [attr.data-cy]="'history-row-' + entry.id"
    [class.opacity-50]="entry.undone || entry.superseded"
  >
    <mat-icon class="!size-4 !text-base text-brand-600">
      {{ isSystem(entry) ? 'bolt' : 'edit' }}
    </mat-icon>

    <div class="flex min-w-0 flex-1 flex-col">
      <span class="truncate text-sm" [attr.data-cy]="'history-label-' + entry.id">
        {{ entry.labelKey | translate: entry.labelParams }}
      </span>

      <span class="text-xs text-brand-600" [matTooltip]="absoluteTime(entry)" [matTooltipPosition]="'above'">
        {{ relativeTime(entry) }} · {{ actorKey(entry) | translate }}
      </span>

      @if (entry.blocked) {
        <div class="mt-1 flex items-start gap-1" [attr.data-cy]="'history-blocked-' + entry.id">
          <mat-icon class="!size-4 !text-base text-amber-600">warning</mat-icon>
          <div class="flex flex-col items-start">
            <span class="text-xs text-amber-700">{{ blockedReasonKey(entry) | translate }}</span>
            <button mat-button class="!px-0 !text-xs" data-cy="history-blocked-restore" (click)="showSnapshots.emit()">
              {{ 'store-history.restore-instead' | translate }}
            </button>
          </div>
        </div>
      }
    </div>

    @if (isPrimary(entry)) {
      <button
        mat-flat-button
        class="!h-7 !text-xs"
        [attr.data-cy]="'history-undo-' + entry.id"
        [disabled]="busy()"
        (click)="onUndo()"
      >
        {{ 'common.undo' | translate }}
      </button>
    } @else if (canUndoTo(entry)) {
      <button
        mat-button
        class="!h-7 !text-xs"
        [attr.data-cy]="'history-undo-to-' + entry.id"
        [disabled]="busy()"
        (click)="onUndoTo(entry)"
      >
        {{ 'store-history.undo-to-here' | translate }}
      </button>
    }
  </div>
</ng-template>
